import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { saveAs } from 'file-saver';

import LinearProgress from '@mui/material/LinearProgress';

import api from '@/services/api';
import { formatTimeDate } from "@/utils/functions/formatTimeDate";

import Alert from '@/views/components/Alert/Alert';
import Header from '@/views/components/Header/Header';
import Footer from '@/views/components/Footer/Footer';

import styles from '@/views/pages/GenerateReport/GenerateReport.module.scss';

const GenerateReport = () => {
	const { dict } = useSelector((state) => state.app);

	const interval = useRef(null);

	const [ pendingRequest, setPendingRequest ] = useState(false);
	const [ error, setError ] = useState('');
	const [ reportData, setReportData ] = useState(null);
	const [ generatingProgress, setGeneratingProgress ] = useState(0);
	const [ generatingInProgress, setGeneratingInProgress ] = useState(false);

	const updateProgress = () => {
		if(pendingRequest) { return; }

		setPendingRequest(true);
		api.get('letters/reports/last/info', { ignoreRedirectInterceptors: true }).then((response) => {
			if (response.status === 200) {
				setReportData(response.data);
				if(response.data.progress < 100) {
					setGeneratingInProgress(true);
					setGeneratingProgress(response.data.progress);
				} else if (response.data.progress === 100) {
					setGeneratingInProgress(false);
				}
				setPendingRequest(false);
			}
		});
	};

	useEffect(() => {
		updateProgress();
	}, []);

	useEffect(() => {
		if (generatingInProgress) {
			interval.current = setInterval(() => {
				updateProgress();
			}, 1000);
		} else {
			clearInterval(interval.current);
			interval.current = null;
		}

		return () => {
			clearInterval(interval.current);
		};
	}, [ generatingInProgress ]);

	const handleGeneratingReport = () => {
		setError('');
		api.get('letters/reports/personal-data').then(() => {
			setGeneratingInProgress(true);
			setGeneratingProgress(0);
			setTimeout(updateProgress, 1000);
		}).catch((error) => {
			setError(error?.response?.data?.error);
		});
	};

	const handleDownloadingReport = () => {
		api.get(`letters/reports/last`, {
			responseType: 'blob',
		}).then((response) => {
			let fileName = response.headers["content-disposition"].split("filename=")[1];
			fileName = fileName.replaceAll('"','');
			saveAs(response.data, fileName);
		});
	};

	return (
		<div className={ styles['generate-report__wrapper'] }>
			<Header narrow={ false } />
			<section className={ styles['generate-report__top'] }>
				<h1 className={ styles['generate-report__top__header'] }>{ dict.report.title }</h1>
				<p className={ styles['generate-report__top__description'] }>{ dict.report.description }</p>
				<button 
					className={ `button__blue${ generatingInProgress ? ' disabled' : ''} ${ styles['generate-report__top__button'] }` }
					onClick={ () => { handleGeneratingReport(); } }
				>
					{ dict.report.generate }
				</button>
			</section>
			{ error && <section className={ styles['generate-report__error'] }>
				<Alert
					variant='error'
					alertContent={ <><strong>{ dict.report.error }</strong>{error}</> }
				/>
			</section> }
			{ reportData && <section className={ styles['generate-report__generated'] }>
				<div className={ styles['generate-report__generated__container'] }>
					{ generatingInProgress ? <>
						<p className={ styles['generate-report__generated--in-progress'] }>{ dict.report.inProgress }</p>
						<div className={ styles['generate-report__generated--progress'] }>
							<LinearProgress
								style={ { width: '100%', alignSelf: 'center' } }
								variant='determinate' 
								value={ generatingProgress } 
							/>
							<span>
								{ generatingProgress }%
							</span>
						</div>
					</> : <>
						<p  className={ styles['generate-report__generated--success'] }><i className='icon-accept-in-circle'/>{ dict.report.ready } ({ formatTimeDate(reportData.modifiedOn) })</p>
						<button 
							className={ `button__blue` }
							onClick={ () => { handleDownloadingReport(); } }
						>
							{ dict.report.download }
						</button>
					</> }
				</div>
			</section> }
			<Footer narrow={ false } />
		</div>
	);
};
export default React.memo(GenerateReport);